import { useMemo } from "react";
import { buildMonthGrid, moveToMonth, toDateKey } from "../../utils/monthGrid";
import styles from "./MiniMonth.module.css";
import { LuArrowLeft, LuArrowRight } from "react-icons/lu";
import { AnimatedReveal } from "@iq/models";

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_NAMES = Array.from({ length: 12 }, (_, index) =>
    new Intl.DateTimeFormat("en-US", { month: "long" }).format(new Date(2000, index, 1)));
const YEAR_SPAN = 5;

function MiniMonth({ selectedDate, weekDateKeys = [], markedDates, onSelectDate }) {
    const [year, month] = selectedDate.split("-").map(Number);
    const monthIndex = month - 1;
    const todayKey = toDateKey(new Date());
    const cells = useMemo(() => buildMonthGrid(year, monthIndex), [year, monthIndex]);
    const weekKeys = useMemo(() => new Set(weekDateKeys), [weekDateKeys]);
    const years = useMemo(
        () => Array.from({ length: YEAR_SPAN * 2 + 1 }, (_, index) => year - YEAR_SPAN + index),
        [year],
    );

    function goToMonth(nextYear, nextMonthIndex) {
        const target = new Date(nextYear, nextMonthIndex, 1);
        onSelectDate(moveToMonth(selectedDate, target.getFullYear(), target.getMonth()));
    }

    return (
        <AnimatedReveal stagger={50} duration={600}>

        <section className={styles.miniMonth} aria-label="Month picker">
            <header className={styles.header}>
                <button type="button" className={styles.navButton} onClick={() => goToMonth(year, monthIndex - 1)} aria-label="Previous month" data-reveal>
                    <LuArrowLeft size={18} />
                </button>
                <div className={styles.pickers} data-reveal>
                    <select
                        className={styles.select}
                        value={monthIndex}
                        onChange={(event) => goToMonth(year, Number(event.target.value))}
                        aria-label="Month"
                    >
                        {MONTH_NAMES.map((name, index) => <option key={name} value={index}>{name}</option>)}
                    </select>
                    <select
                        className={styles.select}
                        value={year}
                        onChange={(event) => goToMonth(Number(event.target.value), monthIndex)}
                        aria-label="Year"
                    >
                        {years.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                </div>
                <button type="button" className={styles.navButton} onClick={() => goToMonth(year, monthIndex + 1)} aria-label="Next month" data-reveal>
                    <LuArrowRight size={18} />
                </button>
            </header>

            <div className={styles.grid} data-reveal>
                {WEEKDAY_LABELS.map((label, index) => (
                    <span key={`${label}-${index}`} className={styles.weekday} aria-hidden="true">{label}</span>
                ))}
                {cells.map(({ date, isCurrentMonth }) => {
                    const key = toDateKey(date);
                    const classNames = [
                        styles.day,
                        !isCurrentMonth && styles.outside,
                        weekKeys.has(key) && styles.inWeek,
                        key === selectedDate && styles.selected,
                        key === todayKey && styles.today,
                    ].filter(Boolean).join(" ");

                    return (
                        <button
                            key={key}
                            type="button"
                            className={classNames}
                            onClick={() => onSelectDate(key)}
                            aria-label={new Intl.DateTimeFormat("en-US", { dateStyle: "full" }).format(date)}
                            aria-pressed={key === selectedDate}
                        >
                            {date.getDate()}
                            {markedDates?.has(key) ? <span className={styles.dot} aria-hidden="true" /> : null}
                        </button>
                    );
                })}
            </div>
        </section>
        </AnimatedReveal>
    );
}

export default MiniMonth;
